import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import {
  debounce,
  EMPTY,
  filter,
  first,
  from,
  groupBy,
  GroupedObservable,
  last,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  race,
  startWith,
  switchMap,
  timer,
} from 'rxjs';

import { runtimeActions, runtimeSelectors, RuntimeState } from '@blaze-runtime/engine-data-access';
import {
  CanvasElement,
  CanvasElementId,
  CanvasElementType,
  PropsOf,
} from '@blaze-runtime/engine-domain';
import {
  RowActionChangePending,
  RuntimeEventContext,
  WorkflowChangePending,
} from '@blaze-shared/elements-domain';
import { loggerCreate } from '@blaze-shared/logger-util';
import { DatasetRowsService } from '@blaze-shared/schema-data-access';
import { getIdColumnName, type RuntimeEventType } from '@blaze-shared/schema-domain';
import { countedShareReplay, delayUntil } from '@blaze-shared/shared-util';
import { Connection, isActionWorkflow, RuntimeElement } from '@blaze-shared/workflow-domain';

import { hasDataProps, hasProps } from '../../canvas-elements/element-model/element-props-guards';
import { isWizard } from '../../canvas-elements/element-model/element-type-guards';
import { findRelatedNamespaces } from '../../canvas-elements/element-model/namespaces';
import { ElementDefinitionsService } from '../../services/element-definitions.service';
import { ElementPaginationService } from '../../services/element-pagination.service';
import { RuntimeService } from '../../services/runtime.service';
import {
  ElementsChangeEvent,
  ElementsFacade,
  getDialogOverlayHostedNs,
  getElementById,
  getElements,
} from '../elements';

import { RuntimeFacade } from './runtime.facade';

/** These element types will be marked as pending when they're the source of execution */
const SOURCE_TYPES_MARKED_AS_PENDING: CanvasElementType[] = [
  'audioRecorder',
  'button',
  'paymentButton',
];

const logUiEvent = loggerCreate('events_ui', 'info', 'UiEffects', null);

@Injectable()
export class UiEffects {
  private readonly actions$ = inject(Actions);
  private readonly datasetRowsService = inject(DatasetRowsService);
  private readonly elementDefinitionService = inject(ElementDefinitionsService);
  private readonly elementPaginationService = inject(ElementPaginationService);
  private readonly elementsFacade = inject(ElementsFacade);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly service = inject(RuntimeService);
  private readonly store = inject(Store<RuntimeState>);

  private readonly elementPaginationActionsBase$ = merge<
    Observable<ElementsChangeEvent<'list' | 'table' | 'calendar' | 'chat' | 'dropdown'>>[]
  >(
    this.elementsFacade.watchElementsOfType('table'),
    this.elementsFacade.watchElementsOfType('list'),
    this.elementsFacade.watchElementsOfType('calendar'),
    this.elementsFacade.watchElementsOfType('chat'),
    this.elementsFacade.watchElementsOfType('dropdown'),
  ).pipe(countedShareReplay());

  /**
   * Runtime event coming from the UI layer detected.
   * Parse event and determine next steps that need to be taken.
   */
  uiSourceEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.runtimeUiEvent),
      concatLatestFrom(() => [
        this.store.select(getDialogOverlayHostedNs),
        this.store.select(runtimeSelectors.selectPageId),
        this.store.select(getElements),
      ]),
      map(([event, dialogOverlayHostedNs, pageId, elements]) => {
        // Prepare required data
        const relatedNamespaces = findRelatedNamespaces(elements, pageId || '');
        return { ...event, dialogOverlayHostedNs, elements, pageId, relatedNamespaces };
      }),
      filter(({ dialogOverlayHostedNs, elements, eventType, ns, pageId, eventContext }) => {
        return this.filterOutUnwantedEvents({
          dialogOverlayHostedNs,
          elements,
          eventType,
          ns,
          pageId,
          eventContext,
        });
      }),
      // The events are grouped (elementId + eventType) to make the debouncing work independently for each element and event type
      // so no events are lost if multiple elements emit something
      groupBy(({ elementId, eventType }) => `${elementId}:${eventType}`, {
        duration: (group$) => this.debounceDuplicateEventsFromSameElement(group$),
      }),
      mergeMap((group$) =>
        group$.pipe(
          // Wait until group gets closed before emitting the event
          last(),
          delayUntil(() =>
            this.store.select(runtimeSelectors.selectIsRunning).pipe(first(Boolean)),
          ),
          concatLatestFrom(() => this.store.select(runtimeSelectors.selectPageId)),
          // Prevents debounced events from the previous page being processed after page change (BL-6432)
          filter(([{ pageId }, newPageId]) => pageId === newPageId),
          // Find and prepare connections to execute
          mergeMap(
            ([
              {
                elementId,
                eventType,
                data,
                ns,
                schema,
                workflowId,
                isInitialData,
                relatedNamespaces,
              },
            ]) => {
              logUiEvent('uiSourceEvent$', (tag) =>
                console.log(`${tag} - Event coming from UI layer detected.`, {
                  elementId,
                  eventType,
                  data,
                  schema,
                  workflowId,
                  isInitialData,
                }),
              );
              const connections: Connection[] = this.createConnectionListToExecute({
                elementId,
                eventType,
                isInitialData,
                relatedNamespaces,
                workflowId,
              });
              // Split connections into separate streams so that they're dispatched as separate observable events.
              const streams = [];
              for (const connection of connections) {
                streams.push({ connection, ns, isInitialData });
              }
              return from(streams);
            },
          ),
        ),
      ),
      filter(({ connection }) => !!connection),
      // Group the connections to ensure that from the list of connections targeting specific workflow, only the last one
      // triggers the running/the refresh of that workflow. This is done to avoid workflows reloading multiple times
      // when not all inputs have set their default values. In effect, we're waiting for all inputs targeting specific
      // workflow to be set before running the workflow.
      groupBy(
        ({ connection }) => {
          const groupId = `${connection.target?.layerType}:${connection.target?.id}`;
          return groupId;
        },
        {
          // Debounce events and then close the group to trigger the emission
          duration: (group$) => this.debounceConnectionsToSameTarget(group$),
        },
      ),
      // This has to be a mergeMap instead of switchMap. If it's a switchMap, it's possible that a new group will
      // cause the cancellation of the flow for the previous group.
      mergeMap((group$) =>
        group$.pipe(
          last(),
          concatLatestFrom(() => this.store.select(runtimeSelectors.selectIsRunning)),
          // Make sure runtime is still running (BL-6432)
          filter(([, isRunning]) => isRunning),
          switchMap(([{ connection, ns }]) => {
            const actionsToDispatch: Action[] = [];
            if (connection) {
              // Queue connections which were triggered by the event from the UI layer.
              actionsToDispatch.push(
                runtimeActions.queueConnections({ ns, connections: [connection] }),
              );
            }
            return actionsToDispatch;
          }),
        ),
      ),
    ),
  );

  /**
   * Query event. Find workflow which provides data to the element and re-run it.
   */
  uiQueryEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.runtimeUiEvent),
      filter(
        ({ eventType, data }) =>
          eventType === 'query' && data?.dataSource?.sourceType === 'context',
      ),
      concatLatestFrom(() => this.store.select(runtimeSelectors.selectIsRunning)),
      filter(([_, isRunning]) => isRunning),
      mergeMap(([{ elementId, ns, data, schema }]) => {
        console.log(`pagination event detected`, elementId, data, schema);
        const actions = this.getTriggerWorkflowActions(ns, data?.dataSource?.id);
        return actions;
      }),
    ),
  );

  /**
   * Trigger wf assigned to the target step
   */
  wizardNextStepEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(runtimeActions.runtimeUiEvent),
      filter(({ eventType }) => eventType === 'next'),
      concatLatestFrom(({ elementId }) => [
        this.elementsFacade.getElement(elementId),
        this.store.select(runtimeSelectors.selectIsRunning),
      ]),
      filter(([_event, element, isRunning]) => isRunning && isWizard(element)),
      mergeMap(([{ data, ns }, element]) => {
        if (!hasProps<PropsOf<'wizard'>>(element)) {
          return EMPTY;
        }
        const currentStepIdx = data.currentStepIdx as number;
        const currentStep = element.props.content.steps?.items?.[currentStepIdx];
        const workflowId = currentStep?.nextAction?.workflowId;
        const actions = this.getTriggerWorkflowActions(ns, workflowId);
        return actions;
      }),
    );
  });

  /**
   * Runtime event which targets UI layer detected.
   */
  uiTargetEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.runtimeEvent),
      filter(({ event }) => event.connection.target.layerType === 'Ui'),
      map(({ event }) => {
        logUiEvent('uiTargetEvent$', (tag) =>
          console.log(`${tag} - Event targetting UI layer detected.`, event),
        );
        return runtimeActions.connectionStepComplete({ event, workflowExecutionId: null });
      }),
    ),
  );

  /**
   * Marking the Ui elements as pending for workflow that is being applied
   */
  uiPendingForWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.markPendingUiWorkflow),
      concatLatestFrom(() => this.elementsFacade.entities$),
      map(([{ event, workflowId }, entities]) => {
        const elementIds: CanvasElementId[] = [];
        const sourceElement = entities[event.connection.source.id];
        const sourceType = sourceElement?.type;
        if (
          event.connection.source.layerType === 'Ui' &&
          sourceType &&
          SOURCE_TYPES_MARKED_AS_PENDING.includes(sourceType)
        ) {
          elementIds.push(event.connection.source.id);
        }
        const chain = this.service.createConnectionChain(event.connection);

        const targetedUiIds = chain
          .filter(({ target, autostart }) => !autostart && target.layerType === 'Ui')
          .map(({ target }) => target.id);
        elementIds.push(...targetedUiIds);

        let changePending: RowActionChangePending | WorkflowChangePending;
        const idColumnName = getIdColumnName(event.schema);
        const rowId = idColumnName && event.data ? (event.data as any)[idColumnName] : undefined;
        if (isActionWorkflow(event.connection, workflowId) && sourceElement?.id) {
          elementIds.push(sourceElement.id);
          changePending = { cause: 'row-action', workflowId, rowId };
        } else {
          changePending = { cause: 'workflow', workflowId };
        }
        return runtimeActions.markPendingUi({ elementIds, changePending });
      }),
    ),
  );

  /**
   * Marking the Ui elements as pending by pending dataset
   */
  uiPendingByDataset$ = createEffect(() =>
    this.datasetRowsService.observeDatasetChangePending().pipe(
      concatLatestFrom(() => this.elementsFacade.elements$),
      mergeMap(([{ cause, datasetId }, elements]) => {
        const affectedUi = new Set<CanvasElementId>();

        // Find dataset in elements' data-bindings
        elements.forEach((element) => {
          if (hasDataProps(element)) {
            (element.props.data || []).forEach((data) => {
              if (data?.dataSource?.sourceType === 'dataset' && data.dataSource.id === datasetId) {
                affectedUi.add(element.id);
              }
            });
          }
        });

        // Find where dataset is used in workflows and mark all connected Ui elements
        const workflows = this.service.getWorkflowsToRefreshAfterDatasetChange(datasetId);
        const connectionsFromWorkflows = workflows
          .flatMap(({ id }) =>
            this.runtimeFacade.getConnectionBySource({
              id,
              layerType: 'Workflow',
            } as RuntimeElement),
          )
          .filter(Boolean) as Connection[];
        const mergedChains = connectionsFromWorkflows.flatMap((conn) =>
          this.service.createConnectionChain(conn),
        );
        mergedChains.forEach((conn) => {
          if (conn.target.layerType === 'Ui') {
            affectedUi.add(conn.target.id);
          }
        });

        const elementIds: CanvasElementId[] = [...affectedUi];

        return this.datasetRowsService.observeDatasetChangeFinalized().pipe(
          filter(
            (finalizeData) => finalizeData.datasetId === datasetId && finalizeData.cause === cause,
          ),
          map(() =>
            runtimeActions.markPendingUiFinish({ elementIds, changePending: { cause, datasetId } }),
          ),
          startWith(
            runtimeActions.markPendingUi({ elementIds, changePending: { cause, datasetId } }),
          ),
        );
      }),
    ),
  );

  /**
   * Initialize pagination state for new paginable elements
   */
  uiInitializeElementPagination$ = createEffect(() =>
    this.elementPaginationActionsBase$.pipe(
      mergeMap((elements$) =>
        elements$.pipe(
          filter(({ type }) => type === 'init'),
          mergeMap(({ element }) => {
            const actions: Action[] = [];
            const elementId = element.id;
            const props = this.elementPaginationService.getPaginationProps(element);
            if (props) {
              actions.push(runtimeActions.initializeElementQuery({ elementId, props }));
            }
            return actions;
          }),
        ),
      ),
    ),
  );

  /**
   * Remove pagination state for non existent elements
   */
  uiRemoveElementPagination$ = createEffect(() =>
    this.elementPaginationActionsBase$.pipe(
      mergeMap((elements$) =>
        elements$.pipe(
          filter(({ type }) => type === 'removed'),
          map(({ element }) => runtimeActions.removeElementQuery({ elementId: element.id })),
        ),
      ),
    ),
  );

  /**
   * Creates a list of connections which should be executed given the source of the event and the event type.
   */
  private createConnectionListToExecute({
    elementId,
    eventType,
    isInitialData,
    relatedNamespaces,
    workflowId,
  }: {
    elementId: string;
    eventType: RuntimeEventType;
    isInitialData: boolean | undefined;
    relatedNamespaces: string[];
    workflowId: string | undefined;
  }): Connection[] {
    const source: RuntimeElement = { id: elementId, layerType: 'Ui' };

    let connections: Connection[] = this.service.getConnectionsBySourceAndEventType(
      source,
      eventType,
      workflowId,
    );
    let globalFilterConnections: Connection[] = [];
    if (!isInitialData) {
      globalFilterConnections = this.service.getConnectionsAffectedByDataFilter(
        elementId,
        relatedNamespaces,
      );
    }
    connections = connections.concat(globalFilterConnections);
    return connections;
  }

  /**
   * Each runtimeActions.runtimeUiEvent carries connection information. Oftentimes, multiple connections can target the same workflow.
   * In such cases, those connections targeting the same workflow are grouped and debounced so that they don't trigger
   * excessive amount of workflow executions.
   */
  private debounceConnectionsToSameTarget(
    group$: GroupedObservable<
      string,
      {
        connection: Connection;
        isInitialData: boolean | undefined;
        ns: string;
      }
    >,
  ): Observable<{
    connection: Connection;
    isInitialData: boolean | undefined;
    ns: string;
  }> {
    return group$.pipe(
      debounce(({ isInitialData }) => {
        // We need to have timer(0) instead of of(0) so that subsequent very quick uiEvents are grouped and do not
        // trigger workflow execution multiple times.
        const debounceInitialData$ = isInitialData ? timer(500) : timer(0);
        // Terminate the debounce immediately if the runtime stops running (e.g. page navigation) (BL-6432)
        const terminateOnRuntimeStop$ = this.store
          .select(runtimeSelectors.selectIsRunning)
          .pipe(first((isRunning) => !isRunning));

        // Whichever emits first should close the group and emit the last value
        return race([debounceInitialData$, terminateOnRuntimeStop$]);
      }),
    );
  }

  /**
   * Debounces events from a particular element group (identified by element ID and event type) and then closes the
   * group to trigger the emission.
   */
  private debounceDuplicateEventsFromSameElement(
    group$: GroupedObservable<
      string,
      {
        elementId: string;
        eventType: RuntimeEventType;
        pageId: string;
      }
    >,
  ): Observable<{
    elementId: string;
    eventType: RuntimeEventType;
    pageId: string;
  }> {
    return group$.pipe(
      debounce(({ elementId, eventType, pageId }) => {
        // If page id changes (navigation to another page happens) then close the group immediately (BL-6432)
        const pageChanged$ = this.store
          .select(runtimeSelectors.selectPageId)
          .pipe(first((newPageId) => newPageId !== pageId));
        // Debounce the event based on the element's output configuration
        const debounceFromOutputDefinition$ = this.store.select(getElementById(elementId)).pipe(
          first(),
          switchMap((element) => {
            let timeMs: number | undefined;
            if (element?.type) {
              const ioConfig = this.elementDefinitionService.getIoConfig(element.type);
              const output = ioConfig?.outputs?.find((out) => out.eventType === eventType);
              timeMs = output?.debounce ? output.debounce : timeMs;
            }
            return timeMs !== undefined ? timer(timeMs) : of(0);
          }),
        );

        // Whichever emits first should close the group and emit the last value
        return race([debounceFromOutputDefinition$, pageChanged$]);
      }),
    );
  }

  private filterOutUnwantedEvents({
    dialogOverlayHostedNs,
    elements,
    eventType,
    ns,
    pageId,
    eventContext,
  }: {
    dialogOverlayHostedNs: string | undefined;
    elements: CanvasElement[];
    eventType: RuntimeEventType;
    ns: string;
    pageId: string;
    eventContext?: RuntimeEventContext;
  }): boolean {
    /**
     * Don't handle 'query' (pagination/sorting/filtering) and Wizard 'status' events here
     * @see uiQueryEvent$
     */
    if (eventType === 'query' || eventType === 'status' || eventType === 'next') {
      return false;
    }

    if (eventContext?.preventConnectionsTrigger) {
      return false;
    }

    // Ignore the event if it was emitted within a dialog or an overlay which just got closed
    const relatedNamespaces = findRelatedNamespaces(elements, pageId || '');
    if (dialogOverlayHostedNs !== ns && !relatedNamespaces.includes(ns)) {
      return false;
    }

    return true;
  }

  private getTriggerWorkflowActions(ns: string, workflowId: string | undefined): Action[] {
    if (!workflowId) {
      return [];
    }
    const workflow = this.runtimeFacade.getWorkflowById(workflowId);
    if (!workflow) {
      return [];
    }
    // Get the connections for target wf and trigger it
    const connectionsToRefresh = this.service.getDataChangedConnections([workflow]);
    const actions: Action[] = [];
    connectionsToRefresh.forEach((connections, connectionsNamespace) => {
      if (ns === connectionsNamespace) {
        actions.push(runtimeActions.queueConnections({ ns, connections }));
      }
    });
    return actions;
  }
}
